import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNftData } from '@hooks/useNftData';
import { useWalletContext } from '@context/WalletContext';
import { useThemeContext } from '@context/ThemeContext';
import { logError, logInfo } from '@utils/log';
import { useApiClient } from '@hooks/useApiClient';

// Action types
const AppActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_TOP_LOADING: 'SET_TOP_LOADING',
  SET_BUY_LOADING: 'SET_BUY_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_NFT_DATA: 'SET_NFT_DATA',
  SET_BUY_DATA: 'SET_BUY_DATA',
  SET_TOP_DATA: 'SET_TOP_DATA',
  SET_FILTER_DATA: 'SET_FILTER_DATA',
  SET_NEWEST: 'SET_NEWEST',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_INITIALIZATION: 'SET_INITIALIZATION',
  SET_AVAX_PRICE: 'SET_AVAX_PRICE',
};

// Initial state
const initialAppState = {
  nftData: [],
  buyNftData: [],
  topNftData: [],
  loading: false,
  topLoading: false,
  buyLoading: false,
  newest: false,
  error: null,
  isInitialized: false,
  avaxPrice: null,
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case AppActionTypes.SET_LOADING:
      return { ...state, loading: action.loading };

    case AppActionTypes.SET_TOP_LOADING:
      return { ...state, topLoading: action.loading };

    case AppActionTypes.SET_BUY_LOADING:
      return { ...state, buyLoading: action.loading };

    case AppActionTypes.SET_NEWEST:
      return { ...state, newest: action.newest };

    case AppActionTypes.SET_ERROR:
      return { ...state, error: action.error, loading: false };

    case AppActionTypes.CLEAR_ERROR:
      return { ...state, error: null };

    case AppActionTypes.SET_INITIALIZATION:
      return { ...state, isInitialized: action.isInitialized };

    case AppActionTypes.SET_AVAX_PRICE:
      return { ...state, avaxPrice: action.price };

    case AppActionTypes.SET_NFT_DATA:
      return { ...state, nftData: action.data, loading: false };

    case AppActionTypes.SET_BUY_DATA:
      return { ...state, buyNftData: action.data, buyLoading: false };

    case AppActionTypes.SET_TOP_DATA:
      return { ...state, topNftData: action.data, topLoading: false };

    default:
      return state;
  }
};

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialAppState);

  // Get contexts
  const walletContext = useWalletContext();
  const { siteDetails, constants, themeConfig, setError } = useThemeContext();

  // Custom hooks
  const { loadWalletNftData, getUserNfts, getBuyNfts, getTopFundraisers } = useNftData();
  const { get } = useApiClient();

  // Use refs to avoid dependency issues
  const stateRef = useRef();
  stateRef.current = state;

  // Fetch AVAX price once on mount
  useEffect(() => {
    get('/fundraisin/getTokenValue/1/avalanche-2')
      .then(data => {
        if (data?.success) {
          dispatch({ type: AppActionTypes.SET_AVAX_PRICE, price: parseFloat(data.cryptoPrice) });
        }
      })
      .catch(err => logError('Failed to fetch AVAX price:', err));
  }, []);

  // Load wallet-dependent data when wallet changes
  useEffect(() => {
    let isActive = true; // Track if this effect is still active
    
    const loadAppData = async () => {
      console.log('Wallet address changed, loading wallet data:', walletContext.walletAddress);
      try {
        const walletData = await loadWalletNftData(walletContext.walletAddress);
        
        // Only dispatch if this effect is still active (prevents race condition)
        if (isActive) {
          dispatch({ type: AppActionTypes.SET_NFT_DATA, data: walletData.nftData });
          dispatch({ type: AppActionTypes.SET_BUY_DATA, data: walletData.buyNftData });
          dispatch({ type: AppActionTypes.SET_TOP_DATA, data: walletData.topNftData });
          dispatch({ type: AppActionTypes.SET_NEWEST, newest: walletData.newest });
          dispatch({ type: AppActionTypes.SET_INITIALIZATION, isInitialized: true });
        }
      } catch (error) {
        if (isActive) {
          logError('Failed to load wallet data:', error);
        }
      }
    };
    
    loadAppData();
    
    // Cleanup: mark this effect as inactive when wallet changes again
    return () => {
      isActive = false;
    };
  }, [walletContext.walletAddress, walletContext.isWalletConnected]); // Simplified dependencies

  // Refresh functions for individual data types
  const refreshNftData = useCallback(async () => {
    dispatch({ type: AppActionTypes.SET_LOADING, loading: true });
    try {
      const nftData = await getUserNfts(walletContext.walletAddress);
      dispatch({ type: AppActionTypes.SET_NFT_DATA, data: nftData.data });
      dispatch({ type: AppActionTypes.SET_NEWEST, newest: nftData.newest });
    } catch (error) {
      logError('Failed to refresh NFT data:', error);
    }
  }, [walletContext.isValidWallet, walletContext.walletAddress, getUserNfts]);

  const refreshBuyData = useCallback(async (page = 1, limit = 10) => {
    dispatch({ type: AppActionTypes.SET_BUY_LOADING, loading: true });
    try {
      const buyData = await getBuyNfts(walletContext.walletAddress, page, limit);
      dispatch({ type: AppActionTypes.SET_BUY_DATA, data: buyData });
    } catch (error) {
      logError('Failed to refresh buy data:', error);
    }
  }, [walletContext.isValidWallet, walletContext.walletAddress, getBuyNfts]);

  const refreshTopData = useCallback(async () => {
    dispatch({ type: AppActionTypes.SET_TOP_LOADING, loading: true });
    try {
      const topData = await getTopFundraisers(walletContext.walletAddress);
      dispatch({ type: AppActionTypes.SET_TOP_DATA, data: topData });
    } catch (error) {
      logError('Failed to refresh top data:', error);
    }
  }, [walletContext.isValidWallet, walletContext.walletAddress, getTopFundraisers, stateRef.nftData]);

  // Context value - memoized to prevent infinite re-renders
  const contextValue = useMemo(() => ({
    // App State
    ...state,
    // Wallet context (spread carefully to avoid re-creation)
    walletAddress: walletContext.walletAddress,
    isWalletConnected: walletContext.isWalletConnected,
    connectWallet: walletContext.connectWallet,
    disconnectWallet: walletContext.disconnectWallet,
    updateWalletAddress: walletContext.updateWalletAddress,
    isValidWallet: walletContext.isValidWallet,
    etherumProvider: walletContext.etherumProvider,
    jsonProvider: walletContext.jsonProvider,
    // Theme context
    siteDetails: siteDetails,
    themeConfig: themeConfig,
    constants: constants,
    // Loading states
    spinner: state.loading,
    // Data
    avaxPrice: state.avaxPrice,
    nftData: state.nftData,
    buyNftData: state.buyNftData,
    nftTop: state.topNftData, // Legacy compatibility
    newest: state.newest,

    // API Functions
    refreshNftData,
    refreshBuyData,
    refreshTopData,

    // Legacy compatibility
    getNftData: refreshNftData,
    getTopData: refreshTopData,
    getBuyNftData: refreshBuyData,
    previewFile: false,
    setError
  }), [
    state,
    walletContext.walletAddress,
    walletContext.isWalletConnected,
    walletContext.connectWallet,
    walletContext.disconnectWallet,
    walletContext.updateWalletAddress,
    walletContext.isValidWallet,
    walletContext.etherumProvider,
    walletContext.jsonProvider,
    siteDetails,
    themeConfig,
    constants,
    refreshNftData,
    refreshBuyData,
    refreshTopData,
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  logInfo('useAppContext called - Stack trace:', context,  new Error().stack?.split('\n')[2]?.trim() || 'Unknown');
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};